import { safeParseFloat } from './CommonUtils';
import { isSEBusinessSegment } from './TotalVolumes';

// Define constants to avoid duplication
const GRID_ID_PROPERTY = "Grid ID";
const GRID_INTENSITY_PROPERTY = "Sales Grid Intensity (gCO2e/kWh)";
const GRID_REGION_PROPERTY = "grid-region";
const RESIDUAL_GRID_PROPERTY = "residual-grid";
const BUSINESS_SEGMENT_PROPERTY = "businessSegment";
const COUNTRY_PROPERTY = "country";
const TWH_UNITS = "TWh";
const GWH_UNITS = "GWh";
const RESIDUAL_GRID_INTENSITY_PROPERTY = "residualGridIntensity";
const CALCULATION_FORMULA = "Sum(Volume × Intensity) ÷ Sum(Volume)";

// Define interfaces for type safety
export interface GridRegionData {
  [COUNTRY_PROPERTY]?: string;
  [GRID_REGION_PROPERTY]?: string;
  [RESIDUAL_GRID_PROPERTY]?: number;
  [BUSINESS_SEGMENT_PROPERTY]?: string;
  [key: string]: string | number | undefined;
}

export interface ResidualGridIntensity {
  [GRID_ID_PROPERTY]?: string;
  [GRID_INTENSITY_PROPERTY]?: string | number;
  [key: string]: string | number | undefined;
}

export interface GridIntensityMap {
  [gridId: string]: number;
}

export interface CountryData {
  totalResidualVolume: number;
  weightedIntensitySum: number;
}

export interface GridRegionDetail {
  gridRegion: string;
  residualVolume: number;
  intensity: number;
  weightedValue: number;
  originalIntensityData: ResidualGridIntensity | null;
  isSE: boolean;
}

export interface CalculationDetails {
  country: string;
  gridRegions: GridRegionDetail[];
  totalResidualVolume: number;
  weightedIntensitySum: number;
  weightedAverage: number;
  formula: string;
  units: string;
}

export interface CountryWeightedResult {
  countryData: CountryData;
  details: CalculationDetails;
}

export interface CountryIntensity {
  [RESIDUAL_GRID_INTENSITY_PROPERTY]: number;
  calculationDetails?: CalculationDetails;
  isSE?: boolean;
}

export interface CountryIntensities {
  [country: string]: CountryIntensity;
}

/**
 * Calculate residual grid intensities by country
 * @param processedData - Processed grid region data
 * @param residualGridIntensitiesData - Grid intensity data
 * @returns Residual intensities by country with calculation details or error message
 */
export function calculateResidualIntensities(
  processedData: GridRegionData[], 
  residualGridIntensitiesData: ResidualGridIntensity[]
): CountryIntensities | string {
  try {
    // Create grid intensity map
    const gridIntensityMap = createGridIntensityMap(residualGridIntensitiesData);
    
    // Get unique countries from processed data
    const countries = new Set(
      (Array.isArray(processedData) ? processedData : [])
        .map(item => item[COUNTRY_PROPERTY])
        .filter((country): country is string => !!country)
    );
    
    // Calculate weighted intensities for each country
    const carbonIntensities: CountryIntensities = {};
    
    countries.forEach(country => {
      // Determine if this is an SE business segment
      const countryGridData = processedData.filter(item => item[COUNTRY_PROPERTY] === country);
      const isSE = countryGridData.length > 0 && 
        countryGridData.some(item => item[BUSINESS_SEGMENT_PROPERTY] && 
            isSEBusinessSegment(item[BUSINESS_SEGMENT_PROPERTY]));
      
      // Calculate weighted intensity sum for this country and get calculation details
      const { countryData, details } = calculateCountryWeightedIntensitySum(
        processedData,
        country,
        gridIntensityMap,
        residualGridIntensitiesData,
        isSE
      );
      
      // Calculate final intensity
      carbonIntensities[country] = calculateCountryIntensity(
        countryData, 
        gridIntensityMap,
        country
      );
      
      // Add calculation details to the intensity object
      carbonIntensities[country].calculationDetails = details;
      
      // Add SE flag to help with interpreting units in UI/reporting
      carbonIntensities[country].isSE = isSE;
    });
    
    return carbonIntensities;
  } catch (error) {
    return error instanceof Error ? error.message : "Error calculating residual intensities";
  }
}

/**
 * Process grid intensity data to create a map
 * @param residualGridIntensitiesData - Grid intensities data
 * @returns Map of grid IDs to intensity values
 */
export function createGridIntensityMap(residualGridIntensitiesData: ResidualGridIntensity[]): GridIntensityMap {
  const gridIntensityMap: GridIntensityMap = {};
  
  if (!Array.isArray(residualGridIntensitiesData)) {
    return gridIntensityMap;
  }
  
  residualGridIntensitiesData.forEach(item => {
    if (!item) {
      return;
    }
    
    const gridId = item[GRID_ID_PROPERTY];
    if (!gridId) {
      return;
    }
    
    const intensityValue = item[GRID_INTENSITY_PROPERTY];
    gridIntensityMap[gridId] = safeParseFloat(String(intensityValue));
  });
  
  return gridIntensityMap;
}

/**
 * Calculate weighted intensity sum for a country with calculation details
 * @param processedData - Processed data for all grid regions
 * @param country - Country to calculate for
 * @param gridIntensityMap - Grid intensity map
 * @param residualGridIntensitiesData - Original grid intensities data for reference
 * @param isSE - Whether this is an SE business segment
 * @returns Volume and weighted sum for country with calculation details
 */
export function calculateCountryWeightedIntensitySum(
  processedData: GridRegionData[], 
  country: string, 
  gridIntensityMap: GridIntensityMap, 
  residualGridIntensitiesData: ResidualGridIntensity[],
  isSE = false
): CountryWeightedResult {
  const countryData: CountryData = {
    totalResidualVolume: 0,
    weightedIntensitySum: 0
  };
  
  // Store calculation details for validation
  const details: CalculationDetails = {
    country,
    gridRegions: [],
    totalResidualVolume: 0,
    weightedIntensitySum: 0,
    weightedAverage: 0,
    formula: CALCULATION_FORMULA,
    units: isSE ? TWH_UNITS : GWH_UNITS
  };
  
  if (!Array.isArray(processedData)) {
    return { countryData, details };
  }
  
  // Get all grid regions for this country with positive residual volume
  const relevantGrids = processedData
    .filter(item => item[COUNTRY_PROPERTY] === country && (item[RESIDUAL_GRID_PROPERTY] ?? 0) > 0);
  
  relevantGrids.forEach(item => {
    const gridRegion = item[GRID_REGION_PROPERTY] as string;
    const residualVolume = item[RESIDUAL_GRID_PROPERTY] ?? 0;
    const businessSegment = item[BUSINESS_SEGMENT_PROPERTY] ?? "";
    
    // Only include entries with positive residual volume
    if (residualVolume <= 0) {
      return;
    }
    
    // Try to get intensity from the grid region or country
    const intensity = gridIntensityMap[gridRegion] ?? gridIntensityMap[country] ?? 0;
    
    // Find the original grid intensity data for reference
    const originalIntensityData = residualGridIntensitiesData.find(
      grid => grid[GRID_ID_PROPERTY] === gridRegion
    ) ?? null;
    
    // Update country data - residual volume is already in the right units from TotalVolumes.js
    countryData.totalResidualVolume += residualVolume;
    countryData.weightedIntensitySum += residualVolume * intensity;
    
    // Store grid-specific calculation details
    details.gridRegions.push({
      gridRegion,
      residualVolume,
      intensity,
      weightedValue: residualVolume * intensity,
      originalIntensityData,
      isSE: isSEBusinessSegment(businessSegment)
    });
  });
  
  // Update the totals in details
  details.totalResidualVolume = countryData.totalResidualVolume;
  details.weightedIntensitySum = countryData.weightedIntensitySum;
  details.weightedAverage = countryData.totalResidualVolume > 0 ? 
    countryData.weightedIntensitySum / countryData.totalResidualVolume : 0;
  
  return { countryData, details };
}

/**
 * Calculate weighted average intensity for a country
 * @param countryData - Country volume and weighted sum data
 * @param gridIntensityMap - Grid intensity map
 * @param country - Country name
 * @returns Country intensity data
 */
export function calculateCountryIntensity(
  countryData: CountryData, 
  gridIntensityMap: GridIntensityMap, 
  country: string
): CountryIntensity {
  const { totalResidualVolume, weightedIntensitySum } = countryData;
  
  if (totalResidualVolume > 0) {
    return {
      [RESIDUAL_GRID_INTENSITY_PROPERTY]: weightedIntensitySum / totalResidualVolume
    };
  } else {
    // Fallback to direct country intensity or 0
    return {
      [RESIDUAL_GRID_INTENSITY_PROPERTY]: gridIntensityMap[country] ?? 0
    };
  }
}